import { Database, Globe, HelpCircle } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import type { PepInstance, PepType } from '@/types/infrastructure'
import { PEP_TYPE_LABELS } from '@/types/infrastructure'

const PEP_TYPE_ICONS: Record<PepType, React.ElementType> = {
  mssql: Database,
  postgres: Database,
  api: Globe,
  unknown: HelpCircle,
}

/**
 * Format a date string as relative time (e.g. "12 seconds ago")
 */
function formatRelativeTime(dateString: string): string {
  const now = Date.now()
  const then = new Date(dateString).getTime()
  const diffMs = now - then

  if (diffMs < 0) return 'just now'

  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return `${seconds}s ago`

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

interface PepTableProps {
  readonly peps: readonly PepInstance[]
}

/**
 * Table of PEP instances belonging to a PDP
 */
export function PepTable({ peps }: PepTableProps) {
  if (peps.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-muted-foreground">
        No PEP instances tracked yet
      </div>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>PEP ID</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Last Seen</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {peps.map((pep) => {
          const Icon = PEP_TYPE_ICONS[pep.pepType] ?? HelpCircle
          // Removed 2026-04-29: per-PEP "open admin UI" hyperlink. The
          // Enforcement Admin tab on this same page already iframes the
          // PEP's management console; a separate new-tab link broke the
          // operator out of the PAP shell and matched the staging deploy
          // which intentionally doesn't render this link. Kept the data
          // shape so the heartbeat can still ship adminUrl in metadata —
          // a future feature may use it (e.g. tenant-aware deep-link
          // inside the embedded iframe instead of a new tab).
          return (
            <TableRow key={pep.pepId}>
              <TableCell className="font-mono text-sm">{pep.pepId}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <Icon className="size-3.5 text-muted-foreground" />
                  <span className="text-sm">{PEP_TYPE_LABELS[pep.pepType] ?? pep.pepType}</span>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant={pep.status === 'active' ? 'default' : 'secondary'}>
                  {pep.status === 'active' ? 'Active' : 'Inactive'}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatRelativeTime(pep.lastSeen)}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
